import { useState, useEffect, useCallback, useRef } from "react";
import { CategoryUI, ProductUI } from "@/shared/types";
import { catalogApi } from "../api/CatalogApi";

interface UseCatalogOptions {
  pageSize?: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface LoadingState {
  categories: boolean;
  products: boolean;
  moreProducts: boolean;
  categoriesError: string | null;
  productsError: string | null;
}

export const useCatalog = (
  selectedCategory: string | null = null,
  options: UseCatalogOptions = {},
  cityId?: string,
  outletId?: string | null
) => {
  const { pageSize = 6 } = options;

  const [categories, setCategories] = useState<CategoryUI[]>([]);
  const [products, setProducts] = useState<ProductUI[]>([]);
  const [loading, setLoading] = useState<LoadingState>({
    categories: true,
    products: true,
    moreProducts: false,
    categoriesError: null,
    productsError: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const categoriesRequestIdRef = useRef(0);
  const productsRequestIdRef = useRef(0);

  const loadCategories = useCallback(async () => {
    const requestId = ++categoriesRequestIdRef.current;
    setLoading((prev) => ({
      ...prev,
      categories: true,
      categoriesError: null,
    }));
    try {
      const data = await catalogApi.getCategories(cityId, outletId);
      if (requestId !== categoriesRequestIdRef.current) return;
      setCategories(data);
    } catch (error) {
      if (requestId !== categoriesRequestIdRef.current) return;
      setLoading((prev) => ({
        ...prev,
        categoriesError:
          error instanceof Error ? error.message : "Ошибка загрузки категорий",
      }));
    } finally {
      if (requestId === categoriesRequestIdRef.current) {
        setLoading((prev) => ({ ...prev, categories: false }));
      }
    }
  }, [cityId, outletId]);

  const loadProducts = useCallback(
    async (page: number = 1, append: boolean = false, queryOverride?: string) => {
      const query = queryOverride ?? searchQuery;
      const isLoadingMore = append && page > 1;
      const requestId = ++productsRequestIdRef.current;

      if (isLoadingMore) {
        setLoading((prev) => ({ ...prev, moreProducts: true }));
      } else {
        setLoading((prev) => ({ ...prev, products: true }));
      }

      try {
        const data = await catalogApi.getProducts(
          page,
          pageSize,
          selectedCategory,
          query,
          cityId,
          outletId
        );

        if (requestId !== productsRequestIdRef.current) return;

        if (append) {
          setProducts((prev) => [...prev, ...data.products]);
        } else {
          setProducts(data.products);
        }

        setPagination({
          currentPage: page,
          totalPages: data.totalPages,
          totalItems: data.totalItems,
          hasNextPage: page < data.totalPages,
          hasPreviousPage: page > 1,
        });

        setLoading((prev) => ({ ...prev, productsError: null }));
      } catch (error) {
        if (requestId !== productsRequestIdRef.current) return;
        setLoading((prev) => ({
          ...prev,
          productsError:
            error instanceof Error ? error.message : "Ошибка загрузки товаров",
        }));
        if (!append) {
          setProducts([]);
        }
      } finally {
        if (requestId === productsRequestIdRef.current) {
          if (isLoadingMore) {
            setLoading((prev) => ({ ...prev, moreProducts: false }));
          } else {
            setLoading((prev) => ({ ...prev, products: false }));
          }
        }
      }
    },
    [selectedCategory, searchQuery, pageSize, cityId, outletId]
  );

  const loadMoreProducts = useCallback(async () => {
    if (!pagination.hasNextPage || loading.moreProducts || loading.products)
      return;
    const nextPage = pagination.currentPage + 1;
    await loadProducts(nextPage, true);
  }, [
    pagination.hasNextPage,
    pagination.currentPage,
    loading.moreProducts,
    loading.products,
    loadProducts,
  ]);

  const searchProducts = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const refreshCatalog = useCallback(async () => {
    setSearchQuery("");
    await Promise.all([loadCategories(), loadProducts(1, false, "")]);
  }, [loadCategories, loadProducts]);

  useEffect(() => {
    if (!cityId) {
      categoriesRequestIdRef.current += 1;
      productsRequestIdRef.current += 1;
      setLoading((prev) => ({
        ...prev,
        categories: false,
        products: false,
        categoriesError: null,
        productsError: null,
      }));
      return;
    }
    loadCategories();
  }, [loadCategories, cityId, outletId]);

  useEffect(() => {
    if (!cityId) {
      return;
    }
    loadProducts(1, false);
  }, [selectedCategory, searchQuery, cityId, outletId, loadProducts]);

  return {
    categories,
    products,
    loading,
    pagination,
    refreshCatalog,
    searchProducts,
    loadMoreProducts,
  };
};
